import { getIbanType } from './getIbanType';

export const shortenString = (str: string): string => {
    if (str.length > 16) {
        const firstPart = str.substring(0, 6);
        const lastPart = str.substring(str.length - 6);
        return `${firstPart}...${lastPart}`;
    }
    return str;
};

export const camelCaseToCapitalizedString = (input: string): string => {
    if (!input) return '';
    const spacedString = input.replace(/([a-z])([A-Z])/g, '$1 $2');
    const firstLetterCapitalized = spacedString.charAt(0).toUpperCase();
    const restOfString = spacedString.slice(1).toLowerCase();
    return firstLetterCapitalized + restOfString;
};

export const addSpaceBetween4and5Chars = (str: string): string => {
    return `${str.substring(0, 4)} ${str.substring(4)}`;
};

export const maskCardPanNumber = (panFirst6?: string | null, panLast4?: string | null) => {
    if (panFirst6 && panLast4) {
        return `${addSpaceBetween4and5Chars(panFirst6)}** **** ${panLast4}`;
    }
    return '';
};

// Own accounts of the current client (from GET /ibans) — used to detect the
// direction of a bankTransfer, since the transaction itself carries no direction flag.
export type OwnBankAccount = { iban?: string; type?: string };

type BankParty = { name?: string; iban?: string; accountNumber?: string };

// FAST payments are addressed by sort code and account number, so their debtor and
// creditor carry no IBAN at all — without this fallback the column stays empty for them.
const bankPartyValue = (party?: BankParty): string =>
    (party?.iban && shortenString(party.iban)) || party?.accountNumber || '';

const findOwnAccount = (
    ownAccounts?: OwnBankAccount[],
    iban?: string,
): OwnBankAccount | undefined =>
    iban ? ownAccounts?.find((account) => account.iban === iban) : undefined;

const isSameName = (name?: string, clientName?: string): boolean =>
    Boolean(name && clientName && name.trim().toLowerCase() === clientName.trim().toLowerCase());

// Settlement accounts are returned by GET /ibans without an IBAN, so matching by IBAN alone
// misses every transfer made from or to them — the client name from GET /config is the fallback.
const isOwnParty = (
    party?: BankParty,
    ownAccounts?: OwnBankAccount[],
    clientName?: string,
): boolean =>
    Boolean(findOwnAccount(ownAccounts, party?.iban) || isSameName(party?.name, clientName));

const ownPartyLabel = (party?: BankParty, ownAccounts?: OwnBankAccount[]): string => {
    const account = findOwnAccount(ownAccounts, party?.iban);
    return (account && getIbanType(account.type || '')) || 'Settlement';
};

export const renderBankSenderValue = (
    obj: any,
    ownAccounts?: OwnBankAccount[],
    clientName?: string,
) => {
    switch (obj.bankTransaction?.bankTransactionType) {
        case 'moneyOut':
        case 'lockFunds':
            return 'Pre-fund';
        case 'bankTransfer': {
            const debtor = obj.bankTransaction.debtor;
            const creditor = obj.bankTransaction.creditor;
            if (isOwnParty(debtor, ownAccounts, clientName)) {
                return ownPartyLabel(debtor, ownAccounts);
            }
            if (isOwnParty(creditor, ownAccounts, clientName)) {
                // Incoming transfer: the sender is the external counterparty
                return bankPartyValue(debtor);
            }
            return 'Settlement';
        }
        case 'unlockFunds':
            return 'Settlement';
        case 'moneyIn':
            return bankPartyValue(obj.bankTransaction.debtor);
        default:
            return '';
    }
};

export const renderCardSenderValue = (obj: any) => {
    switch (obj.cardTransaction?.type) {
        case 'authorization':
        case 'purchase':
            return (
                obj.cardTransaction.cardName ||
                maskCardPanNumber(obj.cardTransaction?.panFirst6, obj.cardTransaction?.panLast4)
            );
        case 'refund':
        case 'chargeback':
        case 'reversal':
            return '';
        default:
            return '';
    }
};

export const renderBankRecipientValue = (
    obj: any,
    ownAccounts?: OwnBankAccount[],
    clientName?: string,
) => {
    switch (obj.bankTransaction?.bankTransactionType) {
        case 'bankTransfer': {
            const creditor = obj.bankTransaction.creditor;
            if (isOwnParty(creditor, ownAccounts, clientName)) {
                // Incoming transfer: the recipient is the client's own account
                return ownPartyLabel(creditor, ownAccounts);
            }
            return bankPartyValue(creditor);
        }
        case 'moneyOut':
            return bankPartyValue(obj.bankTransaction.creditor);
        case 'unlockFunds':
        case 'moneyIn':
            return 'Pre-fund';
        case 'lockFunds':
            return 'Settlement';
        default:
            return '';
    }
};

export const renderCardRecipientValue = (obj: any) => {
    switch (obj.cardTransaction?.type) {
        case 'refund':
        case 'chargeback':
        case 'reversal':
            return (
                obj.cardTransaction.cardName ||
                maskCardPanNumber(obj.cardTransaction?.panFirst6, obj.cardTransaction?.panLast4)
            );
        case 'authorization':
        case 'purchase':
            return '';
        default:
            return '';
    }
};
